/**
 * multiSourceMerger — INTEL-AEGIS-CA-1
 *
 * Freshness-aware merge for multi-source AEGIS provenance chains.
 * When an AEGIS call consults multiple retrieval sources that differ
 * in recency, this module:
 *   1. Scores each source by age relative to a configured window.
 *   2. Detects staleness conflicts (sources with divergent freshness bands).
 *   3. Resolves per the declared conflict-resolution policy.
 *   4. Produces a merged provenance record with per-source detail preserved.
 *
 * Conflict-resolution policies (declared by call site via params._sourceConflictPolicy):
 *   FRESHEST_WINS  — highest freshness score wins; stale sources demoted to advisory
 *   CONSENSUS      — all sources must agree within a tolerance band; else flag HITL
 *   EXPLICIT_PRIORITY — caller supplies priority order in params._sourcePriority[]
 *
 * Root-cause addressed: AEGIS-7 multi-source provenance chains broke when sources
 * differed in freshness, causing the provenance record to lose per-source attribution
 * and conflict context.
 */

export const CONFLICT_POLICY = Object.freeze({
  FRESHEST_WINS:      'FRESHEST_WINS',
  CONSENSUS:          'CONSENSUS',
  EXPLICIT_PRIORITY:  'EXPLICIT_PRIORITY',
});

// Sources fresher than this fraction of the window are "fresh".
// Sources older than STALE_FRACTION × window are "stale".
const FRESH_FRACTION = 0.5;
const STALE_FRACTION = 1.5;

/**
 * @typedef {object} RetrievalSource
 * @property {string}  docPath        - Firestore path / URI
 * @property {number}  [timestampMs]  - source timestamp (epoch ms); omit if unknown
 * @property {any}     [value]        - retrieved payload (optional; used for consensus check)
 * @property {number}  [priority]     - explicit priority rank for EXPLICIT_PRIORITY policy (lower = higher priority)
 */

/**
 * @typedef {object} MergedProvenance
 * @property {RetrievalSource[]} authoritative   - sources that contributed to the decision
 * @property {RetrievalSource[]} advisory        - present but demoted due to staleness / conflict
 * @property {string}            conflictPolicy  - policy applied
 * @property {string}            conflictStatus  - 'none' | 'resolved' | 'hitl_required'
 * @property {string|null}       conflictReason  - human-readable conflict description
 * @property {SourceScore[]}     sourceScores    - per-source freshness scoring detail
 */

/**
 * @typedef {object} SourceScore
 * @property {string}  docPath
 * @property {number}  freshnessScore  - 0..1 (1 = freshest possible)
 * @property {string}  band            - 'fresh' | 'acceptable' | 'stale' | 'unknown'
 * @property {number|null} ageMs
 */

/**
 * Score a single source for freshness.
 *
 * @param {RetrievalSource} source
 * @param {number} freshnessWindowMs  - the call's configured freshness window
 * @param {number} nowMs
 * @returns {SourceScore}
 */
export function scoreSource(source, freshnessWindowMs, nowMs = Date.now()) {
  if (!source.timestampMs || !freshnessWindowMs) {
    return { docPath: source.docPath, freshnessScore: 0.5, band: 'unknown', ageMs: null };
  }
  const ageMs = nowMs - source.timestampMs;
  const ratio = ageMs / freshnessWindowMs;
  const score = Math.max(0, 1 - ratio);

  let band = 'acceptable';
  if (ratio <= FRESH_FRACTION)  band = 'fresh';
  if (ratio >= STALE_FRACTION)  band = 'stale';

  return { docPath: source.docPath, freshnessScore: score, band, ageMs };
}

function _detectConflict(scored) {
  const fresh  = scored.filter(s => s.band === 'fresh');
  const stale  = scored.filter(s => s.band === 'stale');
  if (fresh.length > 0 && stale.length > 0) {
    return {
      detected: true,
      reason: `${fresh.length} fresh source(s) vs ${stale.length} stale source(s) in provenance chain`,
    };
  }
  return { detected: false, reason: null };
}

/**
 * Merge multiple retrieval sources into a single provenance record.
 *
 * @param {RetrievalSource[]} sources
 * @param {number} freshnessWindowMs
 * @param {object} [opts]
 * @param {string} [opts.policy]           - CONFLICT_POLICY constant; default FRESHEST_WINS
 * @param {string[]} [opts.priorityOrder]  - for EXPLICIT_PRIORITY: ordered docPath array
 * @param {number} [opts.nowMs]            - override clock (for deterministic tests)
 * @returns {MergedProvenance}
 */
export function mergeProvenance(sources, freshnessWindowMs, opts = {}) {
  if (!Array.isArray(sources) || sources.length === 0) {
    return {
      authoritative:  [],
      advisory:       [],
      conflictPolicy: opts.policy || CONFLICT_POLICY.FRESHEST_WINS,
      conflictStatus: 'none',
      conflictReason: null,
      sourceScores:   [],
    };
  }

  const nowMs = opts.nowMs ?? Date.now();
  const policy = opts.policy || CONFLICT_POLICY.FRESHEST_WINS;

  const scored = sources.map(s => ({
    source: s,
    score:  scoreSource(s, freshnessWindowMs, nowMs),
  }));

  const sourceScores = scored.map(s => s.score);
  const conflict = _detectConflict(sourceScores);

  let authoritative = [];
  let advisory = [];
  let conflictStatus = conflict.detected ? 'resolved' : 'none';
  let conflictReason = conflict.reason;

  if (!conflict.detected) {
    authoritative = scored.map(s => s.source);
  } else {
    switch (policy) {
      case CONFLICT_POLICY.FRESHEST_WINS: {
        const sorted = [...scored].sort((a, b) => b.score.freshnessScore - a.score.freshnessScore);
        const topScore = sorted[0].score.freshnessScore;
        // Sources within 20% of the top score are co-authoritative.
        authoritative = sorted.filter(s => s.score.freshnessScore >= topScore * 0.8).map(s => s.source);
        advisory       = sorted.filter(s => s.score.freshnessScore  < topScore * 0.8).map(s => s.source);
        break;
      }
      case CONFLICT_POLICY.CONSENSUS: {
        // All sources must fall within the same freshness band.
        const bands = new Set(sourceScores.map(s => s.band).filter(b => b !== 'unknown'));
        if (bands.size <= 1) {
          authoritative = scored.map(s => s.source);
          conflictStatus = 'none';
        } else {
          // Cannot reach consensus — flag for HITL.
          authoritative = [];
          advisory = scored.map(s => s.source);
          conflictStatus = 'hitl_required';
          conflictReason = `Sources span freshness bands [${[...bands].join(', ')}]; consensus unreachable`;
        }
        break;
      }
      case CONFLICT_POLICY.EXPLICIT_PRIORITY: {
        const order = Array.isArray(opts.priorityOrder) ? opts.priorityOrder : [];
        const ranked = [...scored].sort((a, b) => {
          const ia = order.indexOf(a.source.docPath);
          const ib = order.indexOf(b.source.docPath);
          const ea = ia === -1 ? Infinity : ia;
          const eb = ib === -1 ? Infinity : ib;
          return ea - eb;
        });
        // Top-priority source is authoritative; others advisory.
        if (ranked.length > 0) {
          authoritative = [ranked[0].source];
          advisory       = ranked.slice(1).map(s => s.source);
        }
        break;
      }
      default: {
        authoritative = scored.map(s => s.source);
      }
    }
  }

  return {
    authoritative,
    advisory,
    conflictPolicy: policy,
    conflictStatus,
    conflictReason,
    sourceScores,
  };
}
